import { Moment } from "moment"
import { reservationKind } from "~/constants"
import { CampingStyleProps, CampProps, SiteProps } from "../interfaces"
import api from "../api2"
import { IAdditionalFee, IAddOnParam } from "~/api2/price_details"
import { validElectric } from "~/types"
import IOrder from "../interfaces/IOrder"
import { LineItemParams } from "~/api2/orders"

export type PriceDetails = {
  nights: number
  nightly_price: number
  frequency_label: string
  subtotal: number
  discounted_subtotal: number
  service_rate: number
  service_fee: number
  tax_rate: number
  tax: number
  total: number
  holiday_nights: number
  holiday_nightly_fee: number
  holiday_total_fee: number
  discounted_holiday_total_fee: number
  weekend_nights: number
  weekend_nightly_fee: number
  weekend_total_fee: number
  discounted_weekend_total_fee: number
  additional_adults: number
  adults_fee: number
  additional_kids: number
  kids_fee: number
  additional_pets: number
  pets_fee: number
  flat_rate_price: number
  additional_fees: Array<IAdditionalFee>
  credit_card_rate: number
  credit_card_fee: number
}

interface CommonData {
  kind: reservationKind
  camp: CampProps
  campingStyle?: CampingStyleProps
  startDate: Moment
  endDate: Moment
  discountSlug?: string
  couponCodes?: string[]
  subtotalOverride?: number | null
  isTaxableEnabled?: boolean
  isPassengerFeesEnabled?: boolean
  isPetsFeeEnabled?: boolean
  isHolidayEnabled: boolean
  isWeekendEnabled: boolean
  isServiceFeeApplied?: boolean
  isCreditCardFeeEnabled?: boolean
  numberOfAdults?: number
  numberOfKids?: number
  numberOfPets?: number
  userElectric: validElectric
  addOns?: Array<IAddOnParam>
  line_items_attributes?: LineItemParams[]
  intervalId?: string
}

interface DataWithSiteIds extends CommonData {
  siteIds: string[]
  site?: never
}

export interface DataWithSite extends CommonData {
  site: SiteProps | undefined
  siteIds?: never
}

type ForSiteDataProps = {
  kind: string
  startDate: string
  endDate: string
  discountSlug: string | null
  couponCodes: string[]
  subtotalOverride: number | null
  isTaxEnabled: boolean
  is_passenger_fees_enabled: boolean
  isPetsFeeEnabled: boolean
  isHolidayEnabled: boolean
  isWeekendEnabled: boolean
  isServiceFeeApplied: boolean
  isCreditCardFeeEnabled?: boolean
  campingStyleId: string | null
  number_of_adults?: number
  number_of_kids?: number
  number_of_pets?: number
  user_electric: validElectric
  add_ons?: IAddOnParam[]
  line_items_attributes?: LineItemParams[]
  interval_id?: string
}

export function priceDetailsFor(data: DataWithSiteIds): Promise<{ orders: IOrder[]; price_details: PriceDetails[] }>
export function priceDetailsFor(data: DataWithSite): Promise<{ order: IOrder; price_detail: PriceDetails }>

export async function priceDetailsFor(data: DataWithSiteIds | DataWithSite): Promise<{
  price_detail?: PriceDetails
  price_details?: PriceDetails[]
  order?: IOrder
  orders?: IOrder[]
}> {
  const mapToEndpointPayload = (data: CommonData): ForSiteDataProps => ({
    kind: data.kind,
    startDate: data.startDate.toISOString(),
    endDate: data.endDate.toISOString(),
    discountSlug: data.discountSlug ?? null,
    couponCodes: data.couponCodes ?? [],
    subtotalOverride: typeof data.subtotalOverride === "number" ? data.subtotalOverride : null,
    isTaxEnabled: data.isTaxableEnabled ?? false,
    is_passenger_fees_enabled: data.isPassengerFeesEnabled ?? false,
    isPetsFeeEnabled: data.isPetsFeeEnabled ?? false,
    isHolidayEnabled: data.isHolidayEnabled,
    isWeekendEnabled: data.isWeekendEnabled,
    isServiceFeeApplied: data.isServiceFeeApplied ?? false,
    isCreditCardFeeEnabled: data.isCreditCardFeeEnabled,
    campingStyleId: data.campingStyle?.id ?? null,
    number_of_adults: data.numberOfAdults,
    number_of_kids: data.numberOfKids,
    number_of_pets: data.numberOfPets,
    user_electric: data.userElectric,
    add_ons: data.addOns ?? [],
    line_items_attributes: data.line_items_attributes ?? [],
    interval_id: data.intervalId,
  })

  if ("siteIds" in data && data.siteIds?.length) {
    return api.PriceDetails.forSites(data.siteIds, mapToEndpointPayload(data))
  } else if ("site" in data && data.site) {
    return api.PriceDetails.forSite(data.site.id, mapToEndpointPayload(data))
  } else {
    throw new Error("Invalid data provided")
  }
}

export default priceDetailsFor
